// utils/api/passwordAPI.ts
import config from '../../config';
import { apiHandler } from './responseHandler';
import logger from '../logging/logger';

const componentName = 'PasswordAPI';

interface AuthResponse {
    userId: number;
    userName: string;
    isAnonymous: boolean;
    email?: string;
    firstName?: string;
    lastName?: string;
    // UserType fields (optional since they may not always be returned)
    userTypeId?: number;
    isAdmin?: boolean;
    isReseller?: boolean;
    isRestricted?: boolean;
    userTypeCode?: string;
    userTypeDescription?: string;
    // Password management
    mustChangePassword?: boolean;
}

interface PasswordUpdateResponse {
    success: boolean;
    message?: string;
}

interface PasswordResetResponse {
    success: boolean;
    message: string;
    emailSent?: boolean;
    token?: string; // Only in development
    expires?: string; // Only in development
}

interface TokenValidationResponse {
    valid: boolean;
    userId: number;
    userName: string;
    email: string;
}

interface EmailCheckResponse {
    exists: boolean;
    isAnonymous: boolean;
    userId: number | null;
    message: string;
}

/**
 * Centralized API service for all password-related operations
 * Handles proper password transmission to secure backend endpoints
 */
export const passwordAPI = {
    /**
     * Authenticate a user with username and plaintext password
     * @param userName The username or email
     * @param password The plaintext password
     * @returns Promise<AuthResponse> User info if successful
     */
    login: async (userName: string, password: string): Promise<AuthResponse> => {
        try {
            logger.log('LogAuth', componentName, 'Authenticating user', { userName });

            const response = await apiHandler.fetch<AuthResponse>(
                `${config.API_URL}/api/user/authenticate`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify({ userName, password }) // Send plaintext password over HTTPS
                }
            );

            if (response.error) throw response.error;

            logger.log('LogAuth', componentName, 'Authentication successful', {
                userId: response.data?.userId,
                userName: response.data?.userName
            });

            return response.data!;
        } catch (error) {
            logger.log('LogError', componentName, 'Authentication failed', {
                userName,
                error: error instanceof Error ? error.message : String(error)
            });
            throw error;
        }
    },

    /**
     * Set a password for a user account
     * @param userId The user ID
     * @param password The plaintext password
     * @param mustChangePassword Optional flag to require password change on next login
     * @returns Promise<boolean> True if successful
     */
    setPassword: async (userId: number | null | undefined, password: string, mustChangePassword: boolean = false): Promise<boolean> => {
        if (!userId || userId <= 0) {
            logger.log('LogError', componentName, 'Invalid userId provided to setPassword', {
                userId,
                userIdType: typeof userId
            });
            throw new Error(`Valid user ID is required. Received: ${userId}`);
        }

        if (!password || password.length < 1) {
            logger.log('LogError', componentName, 'Invalid password provided to setPassword');
            throw new Error('Password is required');
        }

        try {
            const numericUserId = Number(userId);
            // SECURITY: Never log actual passwords - only metadata
            logger.log('LogAuth', componentName, 'Setting password', {
                userId: numericUserId,
                passwordLength: password.length
            });

            const requestBody = {
                userId: numericUserId,
                password,
                mustChangePassword
            };

            // SECURITY: Never log actual password content
            logger.log('LogAuth', componentName, 'Password API request', {
                userId: numericUserId,
                hasPassword: !!password,
                passwordLength: password.length
            });

            const response = await apiHandler.fetch<PasswordUpdateResponse>(
                `${config.API_URL}/api/user/set-password`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify(requestBody)
                }
            );

            if (response.error) {
                logger.log('LogError', componentName, 'Set password API error', {
                    userId: numericUserId,
                    error: response.error
                });
                throw response.error;
            }

            logger.log('LogAuth', componentName, 'Password set successfully', {
                userId: numericUserId,
                success: response.data?.success
            });
            return response.data?.success === true;
        } catch (error) {
            logger.log('LogError', componentName, 'Set password failed', {
                userId: Number(userId),
                error: error instanceof Error ? error.message : String(error)
            });
            throw error;
        }
    },

    /**
     * Request a password reset token via email
     * @param userName The username
     * @param email The email address
     * @returns Promise<PasswordResetResponse>
     */
    requestPasswordReset: async (userName: string, email: string): Promise<PasswordResetResponse> => {
        try {
            logger.log('LogAuth', componentName, 'Requesting password reset', { userName, email });

            const response = await apiHandler.fetch<PasswordResetResponse>(
                `${config.API_URL}/api/user/reset-token`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify({ userName, email })
                }
            );

            if (response.error) throw response.error;

            logger.log('LogAuth', componentName, 'Password reset requested', { userName });
            return response.data!;
        } catch (error) {
            logger.log('LogError', componentName, 'Password reset request failed', error);
            throw error;
        }
    },

    /**
     * Validate a password reset token
     * @param token The reset token from URL/email
     * @returns Promise<TokenValidationResponse>
     */
    validateResetToken: async (token: string): Promise<TokenValidationResponse> => {
        try {
            logger.log('LogAuth', componentName, 'Validating reset token');

            const response = await apiHandler.fetch<TokenValidationResponse>(
                `${config.API_URL}/api/user/validate-token/${token}`,
                {
                    method: 'GET',
                    credentials: 'include'
                }
            );

            if (response.error) throw response.error;

            logger.log('LogAuth', componentName, 'Token validated successfully');
            return response.data!;
        } catch (error) {
            logger.log('LogError', componentName, 'Token validation failed', error);
            throw error;
        }
    },

    /**
     * Reset password using a valid token
     * @param token The reset token
     * @param newPassword The new plaintext password
     * @param userId Optional user ID (for admin resets)
     * @returns Promise<boolean> True if successful
     */
    resetPassword: async (token: string, newPassword: string, userId?: number | null): Promise<boolean> => {
        try {
            logger.log('LogAuth', componentName, 'Resetting password with token');

            const requestBody: any = {
                token,
                newPassword // Send plaintext password
            };

            // Only include userId if it's a valid number
            if (userId && userId > 0) {
                requestBody.userId = Number(userId);
            }

            const response = await apiHandler.fetch<{ success: boolean; message: string }>(
                `${config.API_URL}/api/user/reset-password`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify(requestBody)
                }
            );

            if (response.error) throw response.error;

            logger.log('LogAuth', componentName, 'Password reset successful');
            return response.data?.success === true;
        } catch (error) {
            logger.log('LogError', componentName, 'Password reset failed', error);
            throw error;
        }
    },

    /**
     * Check if an email address is already registered
     * @param email The email address to check
     * @returns Promise<EmailCheckResponse> Information about email existence
     */
    checkEmailExists: async (email: string): Promise<EmailCheckResponse> => {
        try {
            logger.log('LogAuth', componentName, 'Checking if email exists', { email });

            const response = await apiHandler.fetch<EmailCheckResponse>(
                `${config.API_URL}/api/user/check-email-exists`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify({ email })
                }
            );

            if (response.error) throw response.error;

            logger.log('LogAuth', componentName, 'Email check result', {
                email,
                exists: response.data?.exists,
                isAnonymous: response.data?.isAnonymous
            });

            return response.data!;
        } catch (error) {
            logger.log('LogError', componentName, 'Email check failed', error);
            throw error;
        }
    },

    /**
     * Clear the must change password flag for a user
     * Called after user successfully changes their password
     * @param userId The user ID
     * @returns Promise<boolean> True if successful
     */
    clearMustChangePassword: async (userId: number): Promise<boolean> => {
        try {
            logger.log('LogAuth', componentName, 'Clearing must change password flag', { userId });

            const response = await apiHandler.fetch<{ success: boolean }>(
                `${config.API_URL}/api/user/clear-must-change-password`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify({ userId })
                }
            );

            if (response.error) throw response.error;

            logger.log('LogAuth', componentName, 'Must change password flag cleared', { userId });
            return response.data?.success === true;
        } catch (error) {
            logger.log('LogError', componentName, 'Failed to clear must change password flag', error);
            throw error;
        }
    }
};